import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmBarrelClient, FirearmBarrelDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmBarrelClient = () => new FirearmBarrelClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns (all barrels)
export const useFirearmBarrels = () =>
  useQuery({
    queryKey: ['firearmBarrels'],
    queryFn: async () => {
      const response = await getFirearmBarrelClient().searchFirearmBarrels({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Cached lookup for dropdowns (filtered by specific)
export const useFirearmBarrelsBySpecific = (firearmSpecificId: string | undefined) =>
  useQuery({
    queryKey: ['firearmBarrels', firearmSpecificId],
    queryFn: async () => {
      const response = await getFirearmBarrelClient().searchFirearmBarrels({
        filters: { firearmSpecificId },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmBarrelSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmBarrelClient().searchFirearmBarrels(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmBarrelById = (id: string) =>
  useQuery({
    queryKey: ['firearmBarrel', id],
    queryFn: async () => {
      const response = await getFirearmBarrelClient().getFirearmBarrelById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmBarrel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmBarrelDTO) => {
      await getFirearmBarrelClient().createFirearmBarrel(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrels'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmBarrel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmBarrelDTO }) => {
      await getFirearmBarrelClient().updateFirearmBarrel(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrels'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmBarrel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmBarrelClient().deleteFirearmBarrel(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrels'] });
    },
  });
};